/*                                                                             */
/* Specimen Banner card                                                        */
/* Used both on specimen public page, and in review                            */
/* * specimen picture                                                          */
/* * name, code                                                                */
/* * button for dashboard, edit ( + review status change)                      */
/* * name and link to species                                                  */
/*                                                                             */
import React, { Fragment, useState, useContext } from 'react';
import { useLocation } from 'react-router';
import { gql, useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { Card, Button, Badge } from 'react-bootstrap';
import SingleLightbox from 'components/custom/SingleLightbox';
import MultilingualText from 'components/Multilingual/MultilingualText';
import { getPictureLocaleUrl } from 'helpers/Utils';
import IntlMessages from 'helpers/IntlMessages';
import GenericTreeIcon from 'assets/icons/commonsIcon/generic_tree_icon.svg?react';
import {
  SpecimenSpeciesStatus,
  Speciman,
} from 'services/graphqltypes';
import { Link } from 'react-router-dom';
import { UserContext } from 'contexts/user/UserContextProvider';
import NotificationManager from 'components/common/react-notifications/NotificationManager';
import ApproveModal from 'pages/Bioregion/Dashboard/ApproveModal';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import WrenchIcon from 'mdi-react/WrenchIcon';
import { getOsmTagIconAndName, OsmTagType } from 'pages/Mapping/SelectPoi';

// mutation to change review status
const SPECIMEN_STATUS_UPDATE = gql`
mutation specimenStatusUpdate(
  $reviewerId: UUID!
  $specimenId: UUID!
  $status: SpecimenSpeciesStatus!
) {
  updateSpeciman(
    input: {
      patch: { status: $status, reviewerId: $reviewerId }
      id: $specimenId
    }
  ) {
    speciman {
      id
      status
      reviewerId
      reviewer {
        avatarUrl
        username
        name
        id
      }
    }
  }
}

`;

type SpecimanStatusTag = {
  show: boolean;
  colors: Partial<{ [key in SpecimenSpeciesStatus]: string; }>;
  textOverride: Partial<{ [key in SpecimenSpeciesStatus]: string; }>;

}

/** Banner used for Specimen public page + review page */
const SpecimenBannerCard = ({ speciman, showEditDashboardButton = false, showReviewerButtons = false, }: {
  speciman: Speciman;
  showEditDashboardButton?: boolean;
  showReviewerButtons?: boolean;
}): React.ReactElement => {
  const { slug, name, coverPictureUrl, species, code, status, id, isRestingInPeace } = speciman;
  const { currentUser } = useContext(UserContext);
  const intl = useIntl();
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [specimenStatusUpdate] = useMutation(SPECIMEN_STATUS_UPDATE);
  const currentLocation = useLocation();
  const specimenTerritorySlug = speciman.territory?.slug || speciman.territoriesByCoordinatesGeometry.nodes[0]?.slug;

  const picUrl = coverPictureUrl
    ? getPictureLocaleUrl(coverPictureUrl)
    : speciman.specimenAttachmentsBySpecimenId?.nodes[0]?.url
    ;

  // called on click on the Become Reviewer button
  const becomeTheReviewerHandler = () => {
    specimenStatusUpdate({
      variables: {
        specimenId: id,
        reviewerId: currentUser?.id,
        status: 'UNDER_REVIEW',
      }
    }).then(() => {
      NotificationManager.success(
        intl.formatMessage({ id: 'bioregion.specimen.become-reviewer.notice' }),
        null,
        3000,
        null,
        null,
        'filled'
      );
    });
  };

  // called on click on the Needs Improvement button
  const needsImprovementHandler = () => {
    specimenStatusUpdate({
      variables: {
        specimenId: id,
        reviewerId: currentUser?.id,
        status: 'NEEDS_IMPROVEMENT',
      }
    }).then(() => {
      NotificationManager.success(
        intl.formatMessage({ id: 'general.saved' }),
        null,
        3000,
        null,
        null,
        'filled'
      );
    });
  };

  // Component that renders the buttons for reviewer (Become reviewer, Needs Improvement, Approve)
  const ReviewButtons = ({ status }: { status: SpecimenSpeciesStatus }) => {
    let reviewButtons = null; // we must return null in case there are no review buttons, otherwise it is not a valid react component

    switch (status) {
      case 'TO_REVIEW':
        reviewButtons = (
          <div className='d-flex mt-3'>
            <Button className="btn mx-auto" size="sm" variant="primary" onClick={() => becomeTheReviewerHandler()}>
              <IntlMessages id="bioregion.specimen.dashboard.become-reviewer" />
            </Button>
          </div>
        );
        break;
      case 'UNDER_REVIEW':
      case 'NEEDS_IMPROVEMENT':
        reviewButtons = (
          <div className='d-flex mt-3 btn-group'>
            <Link className="btn btn-outline-primary btn-sm" role="button" style={{ minWidth: '0' }} to={`/commons/${specimenTerritorySlug}/specimen/edit/${slug}`}>
              <div className='d-flex align-items-center'>
                <div className='ml-auto mr-1'>
                  <EditOutlineIcon size={20} />
                </div>
                <div className='mr-auto d-md-none d-lg-block text-truncate' style={{ maxWidth: '100%' }}>
                  <IntlMessages id="general.edit" />
                </div>
              </div>
            </Link>
            <Button className="btn" style={{ minWidth: '0' }} size="sm" variant="outline-primary" onClick={() => needsImprovementHandler()}>
              <div className='text-truncate' style={{ maxWidth: '100%' }}>
                <IntlMessages id="bioregion.specimen.NEEDS_IMPROVEMENT" />
              </div>
            </Button>
            <Button className="btn-outline-yellow" style={{ minWidth: '0' }} size="sm" onClick={() => setShowApproveModal(true)}>
              <div className='text-truncate' style={{ maxWidth: '100%' }}>
                <IntlMessages id="bioregion.specimen.dashboard.approve" />
              </div>
            </Button>
          </div>);
        break;
    }
    return reviewButtons as React.ReactElement;

  };

  // content for the status tag (shown in top left of the banner)
  const specimanStatusTag: SpecimanStatusTag = showReviewerButtons
    ? {
      show: !['DRAFT', 'APPROVED_PUBLISHED', 'MAPPING_DRAFT'].includes(status),
      colors: {
        'NEEDS_IMPROVEMENT': 'warning',
        'UNDER_REVIEW': 'primary',
        'TO_REVIEW': 'danger',
      },
      textOverride: {},
    }
    : {
      show: status !== 'APPROVED_PUBLISHED',
      colors: {
        'NEEDS_IMPROVEMENT': 'danger',
        'UNDER_REVIEW': 'primary',
        'TO_REVIEW': 'warning',
        'DRAFT': 'dark',
        'MAPPING_DRAFT': 'dark',
      },
      textOverride: {
        'TO_REVIEW': 'bioregion.specimen.waiting-for-review',
      }
    };

  return (
    <Fragment>
      <Card className="mb-3">

        {/* img and review status tag */}
        <Card.Body className='p-0'>
          {/* banner pic */}
          <div className="position-relative mb-1">
            {picUrl &&
              <SingleLightbox
                thumb={picUrl}
                large={picUrl}
                className="responsive border-0 card-img-top"
                style={{ maxHeight: '70vh', objectFit: 'cover', width: '100%' }}
              />
            }
          </div>

          {/* POI category badge */}
          {speciman.osmTags && 
            <Badge
              pill
              className='d-flex text-normal align-items-center position-absolute badge-top-left justify-content-center badge-primary'
              style={{ height: '30px', minWidth: '8em' }}
            >
              {getOsmTagIconAndName(speciman?.osmTags as OsmTagType, undefined, 'ml-2').iconName}
            </Badge>
          }

          {/* speciman review status badge */}
          {specimanStatusTag.show &&
            <Badge
              pill
              className={
                `d-flex align-items-center position-absolute badge-top-left justify-content-center
              badge-${specimanStatusTag.show && specimanStatusTag.colors[status]}`
              }
              style={{ height: '30px', minWidth: '8em' }}
            >
              <IntlMessages id={(specimanStatusTag.textOverride && specimanStatusTag.textOverride[status]) ? specimanStatusTag.textOverride[status] : `bioregion.specimen.${status}`} />
            </Badge>
          }

          {isRestingInPeace && <Badge
            pill
            className='d-flex align-items-center position-absolute badge-top-left-2 justify-content-center badge-dark-gray'
            style={{ height: '30px', minWidth: '8em' }}
          >
            RIP
          </Badge>}
        </Card.Body>

        {/* specimen info */}
        <Card.Body className='p-2 py-xl-3'>
          <div className="d-flex flex-column">
            {/* title */}
            <div className="font-weight-bold h3 mx-auto mb-0 text-center"><MultilingualText value={name} /></div>

            {/* specimen code */}
            <div className='mt-1 mx-auto text-center'>{code}</div>

            {/* Dashboard and Edit button*/}
            {showEditDashboardButton && !showReviewerButtons &&
              <div className='d-flex mt-3 btn-group w-80 mx-auto'>

                <Link className="btn mx-auto w-50 btn-sm btn-outline-primary" to={`/commons/${specimenTerritorySlug}/specimen/${slug}/dashboard`}>
                  <div className='d-flex align-items-center justify-content-center'>
                    <WrenchIcon size={16} className='ml-lg-auto mr-lg-1' />
                    <div className='mr-auto d-md-none d-lg-block text-truncate' style={{ maxWidth: '100%' }}>
                      <IntlMessages id="event.open-dashboard" />
                    </div>
                  </div>
                </Link>
                <Link className="btn btn-outline-yellow btn-sm w-50" to={`/commons/${specimenTerritorySlug}/specimen/edit/${slug}`}>
                  <div className='d-flex align-items-center justify-content-center'>
                    <EditOutlineIcon size={20} className='ml-lg-auto mr-lg-1' />
                    <div className='mr-auto d-md-none d-lg-block text-truncate' style={{ maxWidth: '100%' }}>
                      <IntlMessages id="general.edit" />
                    </div>
                  </div>
                </Link>
              </div>
            }

            {/* Reviewer buttons*/}
            <div className='w-100'>
              {showReviewerButtons && <ReviewButtons status={status} />}
            </div>
          </div>
        </Card.Body>

        {/* Species section: Only show it when a species was selected */}
        {species &&
          <>
            {/* separator */}
            <div className="border-bottom mb-4 mt-2" />

            <Card.Body className='pt-0 px-2'>
              {/* speciman's species name and scientific name */}
              <Link to={species
                ? `/${currentLocation.pathname.split('/').slice(1, 3).join('/')}/species/${species.slug}`
                : `/commons/${specimenTerritorySlug}/specimen/edit/${slug}`
              }>
                <div className='d-flex flex-row align-items-center'>
                  <div className='ml-auto'>
                    <GenericTreeIcon height={'2.25rem'} className={'mx-1'} />
                  </div>
                  <div className='d-flex flex-column mr-auto ml-2'>
                    {species
                      ? <Fragment>
                        <div className='h4 mb-0'>
                          <MultilingualText value={species.name} />
                        </div>
                        <div className='color-theme-1' >
                          <i>{species.scientificName}</i>
                        </div>
                      </Fragment>
                      : <IntlMessages id={'specimen.unknown-species'} />
                    }
                  </div>
                  <ChevronRightIcon size={35} color='#909090' />
                </div>


              </Link>
            </Card.Body>
          </>
        }


      </Card >

      {/* Approve Modal */}
      <ApproveModal
        showApproveModal={showApproveModal}
        setShowApproveModal={setShowApproveModal}
        specimenId={speciman?.id}
        authorId={speciman?.createdByUserId}
        reviewerId={speciman?.reviewerId}
      />
    </Fragment >
  );
};


export default SpecimenBannerCard;
